import { themeVar as colors } from "../design-tokens";
import { addressesAgree } from "./AddressMismatchPrompt";

/**
 * WHAT AN EDIT COSTS, SAID BEFORE IT COSTS IT (2026-08-07, owner request: "the ability to go back
 * to any of the steps in the workflow to regenerate the file with a different persona or make
 * adjustments without rerunning the entire workflow").
 *
 * Going back to an earlier step became possible the same day (`StepRail`, `canGoToStep`), and that
 * opened a hole this file closes. Once a breadcrumb can take an agent from a finished report to
 * step 1, they can change WHICH HOME the report is about while a comparable set that was read for
 * the previous one is still sitting on step 3, still ticked, still feeding the estimate. There are
 * two wrong ways to handle that and both were on the table:
 *
 *   * carry it forward silently — the report then prices one home off another home's sales, with
 *     nothing on any screen saying so. This is the worst outcome in the product and the register
 *     forbids it outright.
 *   * wipe it silently — the agent goes back to fix a ZIP code, returns to step 3, and their
 *     comparable set is gone with no explanation. They then distrust every other screen.
 *
 * So: state the consequence in one sentence, name what survives, and let the human decide. That is
 * the same rule `AddressMismatchPrompt` follows for the other address conflict in this flow —
 * **never resolve a source conflict silently** — applied to the one the step rail made reachable.
 *
 * THE MATRIX THIS IMPLEMENTS, in full. It is deliberately two rows, not a table of every field:
 *
 *   THE SUBJECT BECOMES A DIFFERENT PROPERTY  ->  the comparable sales, the competing listings and
 *     the subject's own prior sales are cleared, and so is the payload the last report was built
 *     from. All four are answers to the question "which home is this?", and that question has a new
 *     answer. Nothing else moves: the condition ladder, the value adjustments, the extra context
 *     and the homeowner's name are the agent's own words and are not property-specific enough to
 *     throw away on their behalf.
 *
 *   ANY OTHER EDIT  ->  nothing is cleared. A corrected half-bath count, a re-ticked comparable, a
 *     changed condition, a new dollar figure: every one of these changes what the NEXT build will
 *     say, and none of them makes the work already on screen belong to a different house. The
 *     owner's own example, and the reason this is not "any edit invalidates everything": that rule
 *     is easy to write, easy to defend, and would make the wizard unusable.
 *
 * "A different property" is `addressesAgree`, not string equality — the same loose, token-aligned
 * comparison GATE-004 uses. Fixing "Ln" to "Lane", adding the ZIP, or gaining a +4 suffix from a
 * lookup is not a new home, and a prompt that fired on those would be clicked through on reflex
 * within a day and would then be worth nothing when it fired on a real one.
 */
export interface DownstreamImpact {
  /** True only when something on screen would actually have to be discarded. A subject that moved
   * with no comparables behind it invalidates nothing — there is nothing to invalidate. */
  invalidates: boolean;
  /** The address the comparable set was read for, and the one on screen now. Both are printed, so
   * the agent can see the change they are being asked about rather than being told about it. */
  from: string;
  to: string;
  /** How many rows go, split the way the screens split them. Used for the sentence only. */
  comps: number;
  listings: number;
  priorSales: number;
  /** THE ONE PLAIN SENTENCE. Built here rather than in the component so a test can read the rule
   * without a DOM, and so the two places that show it cannot drift into two different promises. */
  sentence: string;
}

const NO_IMPACT: DownstreamImpact = {
  invalidates: false, from: "", to: "", comps: 0, listings: 0, priorSales: 0, sentence: "",
};

/**
 * Does moving from `compsAnchor` to `subjectAnchor` invalidate the comparable work on screen?
 *
 * `compsAnchor` is the subject address the comparable set was actually assembled for, recorded when
 * it was assembled — NOT re-derived from the grid, which is the thing that has just changed. Null
 * means no comparable set has ever been read, so there is nothing downstream and nothing to warn
 * about.
 */
export function describeSubjectChange({
  compsAnchor,
  subjectAnchor,
  comps,
  listings,
  priorSales,
}: {
  compsAnchor: string | null | undefined;
  subjectAnchor: string;
  comps: number;
  listings: number;
  priorSales: number;
}): DownstreamImpact {
  const from = (compsAnchor ?? "").trim();
  const to = subjectAnchor.trim();
  // Nothing to compare, or nothing to lose. Both are the ordinary case and neither is a warning.
  if (!from || !to || addressesAgree(from, to)) return NO_IMPACT;
  const rows = comps + listings + priorSales;
  if (rows === 0) return NO_IMPACT;

  // The sentence names the number, both addresses and — the half that stops this reading as a
  // threat — what is NOT lost. An agent who is told only what breaks will not press the button even
  // when pressing it is right.
  const parts: string[] = [];
  if (comps) parts.push(`${comps} comparable ${comps === 1 ? "sale" : "sales"}`);
  if (listings) parts.push(`${listings} competing ${listings === 1 ? "listing" : "listings"}`);
  if (priorSales) parts.push(`${priorSales} prior ${priorSales === 1 ? "sale" : "sales"} of the home`);
  const list =
    parts.length === 1 ? parts[0]
      : parts.length === 2 ? `${parts[0]} and ${parts[1]}`
      : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;

  return {
    invalidates: true,
    from,
    to,
    comps,
    listings,
    priorSales,
    sentence:
      `The ${list} you have were read for ${from}, not for ${to}, so going on clears them — ` +
      `everything you entered about the home itself, and anything you typed, stays exactly as it is.`,
  };
}

/**
 * The decision itself. Two exits, neither pre-selected, and the destructive one is not the
 * prettier of the two — the same posture as `AddressMismatchPrompt`.
 *
 * "Go back and put the address right" is not a no-op dressed up as a choice: it returns the agent
 * to the screen they were on with the comparable set untouched, which is exactly what they want if
 * they meant to fix a typo rather than change the house.
 */
export function InvalidationPrompt({
  impact,
  onConfirm,
  onCancel,
  cancelLabel = "Go back and put the address right",
}: {
  impact: DownstreamImpact;
  onConfirm: () => void;
  onCancel: () => void;
  cancelLabel?: string;
}) {
  return (
    <section
      role="alertdialog"
      aria-labelledby="vb-invalidate"
      style={{
        marginTop: 18, padding: 22, borderRadius: 12,
        background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`,
      }}
    >
      <h2 id="vb-invalidate" style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
        This is a different home
      </h2>
      <p style={{ margin: "8px 0 0", fontSize: 14, lineHeight: 1.6, color: colors.textPrimary }}>
        {impact.sentence}
      </p>
      <p style={{ margin: "10px 0 0", fontSize: 13, lineHeight: 1.55, color: colors.textSecondary }}>
        To price {impact.to} you will need sales for {impact.to} — upload that export on the
        comparable sales step, or add it to what you have there.
      </p>
      <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button className="vb-btn"
          type="button"
          onClick={onCancel}
          style={{
            padding: "9px 16px", borderRadius: 8, border: "none", background: colors.accent,
            color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer",
          }}
        >
          {cancelLabel}
        </button>
        <button className="vb-btn"
          type="button"
          onClick={onConfirm}
          style={{
            padding: "9px 16px", borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bgSurface, color: colors.textPrimary, fontSize: 13, cursor: "pointer",
          }}
        >
          Clear them and go on
        </button>
      </div>
    </section>
  );
}

/**
 * THE OTHER HALF OF THE SAME PROBLEM: an edit that invalidates NOTHING on screen, but does make the
 * report already sitting behind it out of date.
 *
 * Changing the half-bath count does not touch the comparable set — the matrix above is explicit
 * that it clears nothing — but the PDF the agent is looking at was built before the change, and
 * "rebuild this in another voice" re-sends the payload that built it, unchanged and on purpose (see
 * `ConfirmedSubmission` in `CmaAmbFlow`). Left alone, that is a button which quietly writes a
 * second file from figures the agent has already corrected, and hands it to them as current.
 *
 * So when the live inputs no longer match the ones that built the report, the voice buttons are not
 * rendered at all and this is rendered instead. Not disabled buttons plus a warning: the agent's
 * next move is a real one — regenerate — and it is the only thing on offer here.
 */
export function StaleReportNotice({ onRebuild }: { onRebuild?: () => void }) {
  return (
    <div
      role="status"
      style={{
        marginTop: 12, padding: "14px 16px", borderRadius: 10,
        background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`,
      }}
    >
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: colors.textPrimary }}>
        You have changed something since this report was written, so it — and any other voice built
        from it — would still carry the old figures. Go back to the review and generate again, and
        your change is in every file from then on.
      </p>
      {onRebuild && (
        <button className="vb-btn"
          type="button"
          onClick={onRebuild}
          style={{
            marginTop: 12, padding: "9px 16px", borderRadius: 8, border: "none",
            background: colors.accent, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer",
          }}
        >
          Back to review and rebuild
        </button>
      )}
    </div>
  );
}
